import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight } from '@mui/icons-material';
import type { SidebarItemConfig } from './types';
import { ItemWrapper, ItemLink, SubItemsContainer } from './NewSidebar.styles';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tooltip, Zoom } from '@mui/material';
import type { ThemeModePalette } from 'utils/clientTheme';
import { useAppDispatch } from 'store/hook';
import { updateSelectedModuleState } from 'store/app.slice';
import { useTranslation } from 'react-i18next';

interface SidebarItemProps {
  item: SidebarItemConfig;
  iscollapsed: boolean;
  level?: number;
  parentId?: string;
  parentPath?: string;
  alreadyinflyout?: boolean;
  prevLeft?: number;
  navColors: ThemeModePalette;
}

// Flyout container styled component
const FlyoutContainer = styled.div<{
  top?: number;
  bottom?: number;
  left: number;
  navbarbg: string;
}>`
  position: fixed;
  left: ${(props) => props.left || 64}px; /* Width of collapsed sidebar */
  ${(props) => (props.top !== undefined ? `top: ${props.top}px;` : '')}
  ${(props) => (props.bottom !== undefined ? `bottom: ${props.bottom}px;` : '')}
  min-width: 200px;
  background-color: ${({ navbarbg }) => navbarbg};
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 99999; /* Increased z-index to ensure it's above everything */
  padding: 8px 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const FlyoutLabel = styled.div`
  padding: 8px;
  font-size: 16px;
  font-weight: 500;
  color: #fff;
`;

const ProBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #fff;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
  &:hover {
    opacity: 0.9;
    filter: brightness(1.1);
  }
`;

// Portal component for flyout menu
const FlyoutMenuPortal = ({
  children,
  top,
  left = 64,
  bottom,
  navbarbg,
}: {
  children: React.ReactNode;
  top?: number;
  left?: number;
  bottom?: number;
  navbarbg: string;
}) => {
  return createPortal(
    <FlyoutContainer top={top} bottom={bottom} left={left} navbarbg={navbarbg}>
      {children}
    </FlyoutContainer>,
    document.body
  );
};

export const SidebarItem = ({
  item,
  iscollapsed,
  level = 0,
  parentId,
  parentPath,
  alreadyinflyout = false,
  prevLeft = 0,
  navColors,
}: SidebarItemProps) => {
  const { t } = useTranslation();
  const navKey = item.id.replace(/([A-Z])/g, '_$1').toLowerCase();
  const label = t(navKey, item.label);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);
  const hasChildren = item.children && item.children.length > 0;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const showProBadge = Boolean(item.showPro);

  const location = useLocation();

  const handleProBadgeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate('/get-pro');
  };

  const splitPath = location.pathname.split('/').filter((path) => path !== '');

  const currentIdxPathValue = splitPath[level];

  const isActive = useMemo(() => {
    if (showProBadge && location.pathname === '/get-pro') return true;
    const currentSidebarItemPathValue =
      parentPath && level > 0 ? `${parentPath}/${item.path}` : item.path;
    return location.pathname.includes(currentSidebarItemPathValue ?? '');
  }, [location, showProBadge]);

  const handleToggle = (e: React.MouseEvent) => {
    if (showProBadge) {
      e.preventDefault();
      handleProBadgeClick(e);
      return;
    }
    if (hasChildren && !iscollapsed) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  // Track position for flyout menu
  const [flyoutPosition, setFlyoutPosition] = useState<{
    top?: number;
    bottom?: number;
  }>({
    top: 0,
  });

  // Update position when hovered
  useEffect(() => {
    if (isHovered && itemRef.current) {
      const rect = itemRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Estimate the height of the flyout menu based on children count
      const estimatedFlyoutHeight = item.children
        ? item.children.length * 40 + 60
        : 100; // 40px per item + padding

      // Check if flyout would go below viewport
      if (rect.top + estimatedFlyoutHeight > viewportHeight) {
        // Position from bottom instead of top
        setFlyoutPosition({
          bottom: viewportHeight - rect.bottom,
        });
      } else {
        // Use top positioning as normal
        setFlyoutPosition({
          top: rect.top,
        });
      }
    }
  }, [isHovered, item.children]);

  // If it's a link, render a NavLink
  if (item.path && !hasChildren) {
    return (
      <Tooltip
        title={alreadyinflyout || !iscollapsed ? '' : label}
        placement="right"
        arrow
        slots={{
          transition: Zoom,
        }}
      >
        <ItemLink
          className={isActive && !showProBadge ? 'active' : ''}
          to={
            showProBadge
              ? '/get-pro'
              : parentPath && level > 0
              ? `${parentPath}/${item.path}`
              : item.path
          }
          iscollapsed={!alreadyinflyout && iscollapsed ? true : undefined}
          level={level}
          onClick={() => {
            if (!showProBadge && item.module) {
              dispatch(updateSelectedModuleState(item.module));
            }
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          alreadyinflyout={alreadyinflyout ? true : undefined}
          navbaractivebg={navColors.navbarActiveBg}
          navbaractivesubbg={navColors.navbarActiveSubBg}
          navbaractivecolor={navColors.navbarActiveColor}
        >
          {item.icon && <span className="sidebar-icon">{item.icon}</span>}
          <span className="sidebar-label">{label}</span>
          {showProBadge && !iscollapsed && (
            <Tooltip title={t('get_pro')} placement="right" arrow>
              <ProBadge onClick={handleProBadgeClick}>Pro</ProBadge>
            </Tooltip>
          )}
        </ItemLink>
      </Tooltip>
    );
  }

  // If it has children, render a collapsible section
  return (
    <>
      <div ref={itemRef} style={{ position: 'relative', zIndex: 1 }}>
        <ItemWrapper
          onClick={handleToggle}
          iscollapsed={!alreadyinflyout && iscollapsed}
          level={level}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={
            showProBadge && location.pathname === '/get-pro'
              ? 'active'
              : currentIdxPathValue === parentPath
              ? 'active'
              : ''
          }
          alreadyinflyout={alreadyinflyout ? true : undefined}
          navbaractivebg={navColors.navbarActiveBg}
          navbaractivesubbg={navColors.navbarActiveSubBg}
          navbaractivecolor={navColors.navbarActiveColor}
        >
          <span className="sidebar-icon">{item.icon}</span>
          <span className="sidebar-label">{label}</span>
          {showProBadge && !iscollapsed && (
            <Tooltip title={t('get_pro')} placement="right" arrow>
              <ProBadge onClick={handleProBadgeClick}>Pro</ProBadge>
            </Tooltip>
          )}
          {hasChildren && !iscollapsed && (
            <ChevronRight
              className="chevron"
              style={{
                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
              }}
            />
          )}
        </ItemWrapper>

        {/* Normal expanded view for non-collapsed mode */}
        {hasChildren && !iscollapsed && (
          <SubItemsContainer isOpen={isExpanded}>
            {item?.children?.map((child) => (
              <SidebarItem
                key={child.id}
                item={child}
                iscollapsed={!alreadyinflyout && iscollapsed}
                level={level + 1}
                parentId={item.id}
                parentPath={item.path}
                alreadyinflyout={alreadyinflyout ? true : undefined}
                navColors={navColors}
              />
            ))}
          </SubItemsContainer>
        )}
      </div>

      {/* Flyout menu for collapsed mode using Portal */}
      {hasChildren && iscollapsed && isHovered && (
        <FlyoutMenuPortal
          top={flyoutPosition.top}
          bottom={flyoutPosition.bottom}
          left={level > 0 ? prevLeft + label.length * 11.5 : 64}
          navbarbg={navColors.navbarBg}
        >
          <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <FlyoutLabel>{label}</FlyoutLabel>
            {item?.children?.map((child) => (
              <SidebarItem
                key={child.id}
                item={child}
                iscollapsed={iscollapsed}
                level={level + 1}
                parentId={item.id}
                parentPath={item.path}
                alreadyinflyout={true}
                prevLeft={level > 0 ? prevLeft + label.length * 11.5 : 0}
                navColors={navColors}
              />
            ))}
          </div>
        </FlyoutMenuPortal>
      )}
    </>
  );
};
